import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";

export const APP_DEVELOPER_INTENT_EXAMPLES = [
  "I'm building a new product to offer better loans to my customers",
  "Help me integrate with payment and identity APIs",
  "I'm getting API errors during integration",
] as const;

interface ConciergeIntentFormProps {
  intent: string;
  onIntentChange: (value: string) => void;
  onSubmit: () => void;
  loading?: boolean;
  disabled?: boolean;
}

const ConciergeIntentForm: React.FC<ConciergeIntentFormProps> = ({
  intent,
  onIntentChange,
  onSubmit,
  loading = false,
  disabled = false,
}) => {
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
      <TextField
        fullWidth
        multiline
        minRows={3}
        maxRows={6}
        value={intent}
        onChange={(e) => onIntentChange(e.target.value)}
        placeholder="What are you trying to accomplish?"
        disabled={disabled || loading}
        autoFocus
        InputProps={{
          sx: {
            borderRadius: 2,
            bgcolor: "background.paper",
            fontSize: "1rem",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 1px 6px rgba(0,0,0,0.45)"
                : "0 1px 6px rgba(32,33,36,0.18)",
          },
        }}
      />

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 1,
          mt: 2,
        }}
      >
        {APP_DEVELOPER_INTENT_EXAMPLES.map((example) => (
          <Chip
            key={example}
            label={example}
            size="small"
            clickable
            disabled={loading}
            variant="outlined"
            onClick={() => onIntentChange(example)}
          />
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={disabled || loading || !intent.trim()}
          startIcon={
            loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <AutoAwesomeOutlined />
            )
          }
          sx={{ px: 4 }}
        >
          {loading ? "Finding your path…" : "Get my journey"}
        </Button>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", textAlign: "center", mt: 2 }}
      >
        SAMi Concierge will search the product catalog and recommend next steps for app developers.
      </Typography>
    </Box>
  );
};

export default ConciergeIntentForm;
